import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { clsx } from '../../ds/clsx';
import {
  Banner,
  Button,
  Dialog,
  Pill,
  Skeleton,
  Toggle,
  useToast,
  type PillVariant,
} from '../../ds/components';
import { errorMessage } from '../../lib/api';
import { usePermissions, useSession } from '../../lib/session';
import { CaldavReconnectDialog } from '../settings/CaldavReconnectDialog';
import {
  bookingCalendarOf,
  calendarNames,
  formatPhone,
  isCalendarConnection,
  isIcloudCalendar,
  isMailConnection,
  providerLabel,
  useConnections,
  useDeleteConnection,
  useLinkConnection,
  usePatchCalendar,
  useReconnectConnection,
  useWhatsappStatus,
  type ConnectionItem,
  type ConnectionStatus,
} from '../settings/connections';
import styles from './ChannelCards.module.css';

/** Pill variant per connection status (D-37 texts come from the API). */
export function statusPillVariant(status: ConnectionStatus): PillVariant {
  switch (status) {
    case 'connected':
      return 'success';
    case 'connecting':
      return 'info';
    case 'credentials_required':
      return 'warning';
    case 'disconnected':
    case 'error':
      return 'danger';
  }
}

interface RowProps {
  glyph: string;
  glyphClass: string;
  title: string;
  subtitle: ReactNode;
  tone?: 'sunken' | 'card' | 'warning' | undefined;
  children?: ReactNode;
}

function ChannelRow({ glyph, glyphClass, title, subtitle, tone = 'sunken', children }: RowProps) {
  return (
    <div
      className={clsx(styles.row, tone === 'card' && styles.rowCard, tone === 'warning' && styles.rowWarning)}
    >
      <span className={clsx(styles.glyph, glyphClass)} aria-hidden="true">
        {glyph}
      </span>
      <span className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        <span className={styles.rowSubtitle}>{subtitle}</span>
      </span>
      {children}
    </div>
  );
}

interface RowChrome {
  glyph: string;
  glyphClass: string;
  title: string;
}

const googleChrome: RowChrome = {
  glyph: 'G',
  glyphClass: styles.glyphGoogle ?? '',
  title: 'Google Kalender',
};
const outlookChrome: RowChrome = {
  glyph: 'M',
  glyphClass: styles.glyphMicrosoft ?? '',
  title: 'Microsoft / Outlook',
};
const hostedProviders: ReadonlyArray<RowChrome & { key: 'google' | 'outlook' }> = [
  { key: 'google', ...googleChrome },
  { key: 'outlook', ...outlookChrome },
];

/** Row chrome per connection: the hosted providers as designed, CalDAV/iCloud with the glyphs of Einstellungen 05. */
function rowChrome(item: ConnectionItem): RowChrome {
  if (item.provider === 'google') return googleChrome;
  if (item.provider === 'outlook') return outlookChrome;
  return {
    glyph: isIcloudCalendar(item) ? '◉' : '⌘',
    glyphClass: styles.glyphCaldav ?? '',
    title: item.displayName ?? providerLabel(item.provider),
  };
}

/**
 * Beratung 08 „Kalender": every calendar connection of the user one below the other — Google / Microsoft
 * (hosted) and any number of CalDAV accounts (P-47) — with the writable and conflict sub-toggles of its
 * booking calendar; providers without a connection offer „Verbinden".
 */
export function CalendarConnectionsCard() {
  const navigate = useNavigate();
  const connections = useConnections();
  const link = useLinkConnection();
  const reconnect = useReconnectConnection();
  const remove = useDeleteConnection();
  const patch = usePatchCalendar();
  const { toast } = useToast();
  const [confirm, setConfirm] = useState<ConnectionItem | null>(null);
  const [reconnectItem, setReconnectItem] = useState<ConnectionItem | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function connect(provider: 'google' | 'outlook') {
    setError(null);
    try {
      const result = await link.mutateAsync({ provider, purposes: ['calendar'] });
      window.location.assign(result.url);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  async function reauth(item: ConnectionItem) {
    if (item.provider === 'caldav') {
      setReconnectItem(item);
      return;
    }
    setError(null);
    try {
      const result = await reconnect.mutateAsync(item.id);
      window.location.assign(result.url);
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  async function disconnect() {
    if (!confirm) return;
    try {
      await remove.mutateAsync(confirm.id);
      toast({ message: 'Verbindung getrennt.' });
      setConfirm(null);
    } catch (failure) {
      setError(errorMessage(failure));
      setConfirm(null);
    }
  }

  async function setCalendar(item: ConnectionItem, body: { writable?: boolean; role?: 'block' | 'ignore' }) {
    const calendar = bookingCalendarOf(item);
    if (!calendar) return;
    setError(null);
    try {
      await patch.mutateAsync({ connectionId: item.id, calendarId: calendar.id, body });
    } catch (failure) {
      setError(errorMessage(failure));
    }
  }

  const calendarItems = (connections.data?.items ?? []).filter(isCalendarConnection);
  const caldavItems = calendarItems.filter((item) => item.provider === 'caldav');

  function connectedRow(item: ConnectionItem) {
    const chrome = rowChrome(item);
    const calendar = bookingCalendarOf(item);
    const checksConflicts = calendar ? calendar.role !== 'ignore' : false;
    const subtitle = [
      item.identifier,
      // A CalDAV account usually carries several calendars — name what BRIMO reads.
      item.provider === 'caldav' && item.calendars.length > 1 ? calendarNames(item) : null,
      checksConflicts ? 'Konfliktprüfung aktiv' : 'Konfliktprüfung aus',
    ]
      .filter(Boolean)
      .join(' · ');
    const offersReconnect =
      item.provider === 'caldav'
        ? item.status === 'credentials_required' || item.status === 'error'
        : item.status === 'credentials_required' || item.status === 'disconnected' || item.status === 'error';
    return (
      <div key={item.id} className={styles.stack}>
        <ChannelRow
          glyph={chrome.glyph}
          glyphClass={chrome.glyphClass}
          title={chrome.title}
          subtitle={item.error && item.status !== 'connected' ? item.error : subtitle}
        >
          <Pill variant={statusPillVariant(item.status)} className={styles.pill}>
            {item.statusLabel}
          </Pill>
          {offersReconnect ? (
            <Button
              size="small"
              className={styles.connectButton}
              loading={reconnect.isPending}
              onClick={() => void reauth(item)}
            >
              Neu anmelden
            </Button>
          ) : null}
          <button type="button" className={styles.textLink} onClick={() => setConfirm(item)}>
            Trennen
          </button>
        </ChannelRow>
        {calendar && item.status === 'connected' ? (
          <div className={styles.subToggles}>
            <Toggle
              label="Termine eintragen und Gast einladen"
              size="large"
              checked={calendar.writable}
              disabled={patch.isPending}
              onChange={(checked) => void setCalendar(item, { writable: checked })}
              className={styles.subToggle}
            />
            <span className={styles.subToggleNote}>· aus = nur Konfliktprüfung</span>
            <Toggle
              label="Konflikte prüfen"
              size="large"
              checked={checksConflicts}
              disabled={patch.isPending}
              onChange={(checked) => void setCalendar(item, { role: checked ? 'block' : 'ignore' })}
              className={styles.subToggle}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section className={styles.card} aria-labelledby="calendar-card-title">
      <h2 id="calendar-card-title" className={styles.cardTitle}>
        Kalender
      </h2>
      <p className={styles.cardText}>Konfliktprüfung und automatischer Eintrag beim zugewiesenen Berater.</p>
      {error ? (
        <div className={styles.banner}>
          <Banner tone="danger">{error}</Banner>
        </div>
      ) : null}
      {connections.isError ? (
        <div className={styles.banner}>
          <Banner tone="danger">{errorMessage(connections.error)}</Banner>
        </div>
      ) : null}
      <div className={styles.rows}>
        {connections.isPending ? (
          <>
            <Skeleton variant="block" height={64} />
            <Skeleton variant="block" height={64} />
          </>
        ) : (
          <>
            {hostedProviders.map((provider) => {
              const items = calendarItems.filter((entry) => entry.provider === provider.key);
              if (items.length > 0) return items.map(connectedRow);
              return (
                <ChannelRow
                  key={provider.key}
                  glyph={provider.glyph}
                  glyphClass={provider.glyphClass}
                  title={provider.title}
                  subtitle="Nicht verbunden"
                  tone="card"
                >
                  <Button
                    size="small"
                    className={styles.connectButton}
                    loading={link.isPending}
                    onClick={() => void connect(provider.key)}
                  >
                    Verbinden
                  </Button>
                </ChannelRow>
              );
            })}
            {caldavItems.map(connectedRow)}
            {caldavItems.length === 0 ? (
              <ChannelRow
                key="caldav"
                glyph="⌘"
                glyphClass={styles.glyphCaldav ?? ''}
                title="CalDAV"
                subtitle="Apple iCloud Kalender, Nextcloud, Zimbra · Nicht verbunden"
                tone="card"
              >
                <Button
                  size="small"
                  className={styles.connectButton}
                  onClick={() => navigate('/settings/connections?add=caldav')}
                >
                  Verbinden
                </Button>
              </ChannelRow>
            ) : null}
          </>
        )}
      </div>
      <CaldavReconnectDialog
        item={reconnectItem}
        onClose={() => setReconnectItem(null)}
        onDone={() => {
          setReconnectItem(null);
          toast({ message: 'Verbindung hergestellt.' });
        }}
      />
      <Dialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        eyebrow="Kalender"
        title="Verbindung trennen?"
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Abbrechen
            </Button>
            <Button variant="destructiveFilled" loading={remove.isPending} onClick={() => void disconnect()}>
              Trennen
            </Button>
          </>
        }
      >
        <p className={styles.dialogText}>
          Belegte Zeiten aus {confirm?.identifier ?? 'diesem Kalender'} werden nicht mehr geprüft und Termine
          nicht mehr eingetragen.
        </p>
      </Dialog>
    </section>
  );
}

/** Beratung 08 „Kanäle": WhatsApp session, e-mail sender, own domain (M8: not set up). */
export function ChannelsCard() {
  const navigate = useNavigate();
  const session = useSession();
  const permissions = usePermissions();
  const connections = useConnections();
  const whatsapp = useWhatsappStatus();
  const mailbox = (connections.data?.items ?? []).find(
    (item) => isMailConnection(item) && item.status === 'connected',
  );
  const admin = permissions.has('settings.organization');

  return (
    <section className={styles.card} aria-labelledby="channels-card-title">
      <h2 id="channels-card-title" className={styles.cardTitle}>
        Kanäle
      </h2>
      <p className={styles.cardText}>Bestätigungen und Erinnerungen an die Mandanten.</p>
      {whatsapp.isError ? (
        <div className={styles.banner}>
          <Banner tone="danger">{errorMessage(whatsapp.error)}</Banner>
        </div>
      ) : null}
      <div className={styles.rows}>
        {whatsapp.isPending || connections.isPending ? (
          <>
            <Skeleton variant="block" height={64} />
            <Skeleton variant="block" height={64} />
            <Skeleton variant="block" height={64} />
          </>
        ) : (
          <>
            <ChannelRow
              glyph="W"
              glyphClass={styles.glyphWhatsapp ?? ''}
              title="WhatsApp"
              subtitle={
                whatsapp.data?.state === 'connected'
                  ? `${whatsapp.data.phoneE164 ? formatPhone(whatsapp.data.phoneE164) : 'Session aktiv'} · Bestätigungen und Erinnerungen per WhatsApp`
                  : whatsapp.data?.state === 'disconnected'
                    ? `${whatsapp.data.phoneE164 ? `${formatPhone(whatsapp.data.phoneE164)} · ` : ''}Session unterbrochen`
                    : whatsapp.data && whatsapp.data.state !== 'none'
                      ? 'Session wird aufgebaut'
                      : 'Keine Session offen · QR-Code mit WhatsApp scannen'
              }
              tone={whatsapp.data?.state === 'connected' ? 'sunken' : 'card'}
            >
              <Pill
                variant={
                  whatsapp.data?.state === 'connected'
                    ? 'success'
                    : whatsapp.data?.state === 'disconnected'
                      ? 'danger'
                      : 'neutral'
                }
                className={styles.pill}
              >
                {whatsapp.data?.state === 'connected'
                  ? 'Aktiv'
                  : whatsapp.data?.state === 'disconnected'
                    ? 'Getrennt'
                    : 'Nicht verbunden'}
              </Pill>
              {whatsapp.data?.state === 'connected' || whatsapp.data?.state === 'disconnected' ? (
                <Link to="/settings/whatsapp" className={styles.textLink}>
                  Session
                </Link>
              ) : (
                <Button
                  size="small"
                  className={styles.connectButton}
                  onClick={() => navigate('/settings/whatsapp')}
                >
                  QR-Code anzeigen
                </Button>
              )}
            </ChannelRow>
            <ChannelRow
              glyph="@"
              glyphClass={styles.glyphMail ?? ''}
              title="E-Mail-Versand"
              subtitle={
                mailbox
                  ? `${mailbox.identifier ?? mailbox.displayName ?? ''} · verbundenes Postfach`
                  : `Systemversand der Organisation · Antworten an ${session.account.email}`
              }
            >
              <Pill variant="success" className={styles.pill}>
                Aktiv
              </Pill>
              <Link to="/settings/connections" className={styles.textLink}>
                {mailbox ? 'Verbindungen' : 'Postfach verbinden'}
              </Link>
            </ChannelRow>
            <ChannelRow
              glyph="D"
              glyphClass={styles.glyphDomain ?? ''}
              title="Eigene Absenderdomain"
              subtitle="Nicht eingerichtet · Versand über die Systemadresse"
              tone="card"
            >
              <Pill variant="neutral" className={styles.pill}>
                Nicht eingerichtet
              </Pill>
              {admin ? (
                <Link to="/settings/organization/domain" className={styles.textLink}>
                  Domain
                </Link>
              ) : null}
            </ChannelRow>
          </>
        )}
      </div>
    </section>
  );
}
